import React, { useCallback, useEffect, useMemo } from "react";

import { MdmDeviceDto, MdmObjectDto, SortDirection } from "gql/generated/types";
import { DataTableStateEvent } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { DATE_FORMATTER_SECONDS } from "utils/constants";
import { fmtDatetime } from "utils/time-utils";
import { useTimezone } from "hooks/useTimezone";
import useProjectContext from "../../hooks/useProjectContext";
import useTableFilter from "../../hooks/useTableFilter";
import useUserTableDetails from "../../hooks/useUserTableDetails";
import { measurementOrder } from "../../utils/const";
import { MdmTableName, TableColumn } from "../../utils/types";
import {
  apolloErrorToaster,
  measurementUnits,
  renderDeviceStatus,
  renderLastMeasurementValue,
  renderObjectLinkInDevices,
} from "../../utils/utils";
import MapWrapper from "../MapWrapper";
import ObjectAndDeviceTableWrapper from "../ObjectAndDeviceTableWrapper";
import ObjectMapContent from "../ObjectMapContent";

type DevicesContentProps = {
  tableName: MdmTableName;
  useGetDevicesPagedLazyQuery: any;
  useGetDevicesPagedQuery: any;
  dataKey: string;
};

const DevicesContent: React.FC<DevicesContentProps> = ({
  tableName,
  useGetDevicesPagedLazyQuery,
  useGetDevicesPagedQuery,
  dataKey,
}) => {
  const tz = useTimezone();
  const { projectId, project, userSettings } = useProjectContext();
  const tableDetails = useUserTableDetails(userSettings, tableName);
  const [tableOptions, setTableOptions] = useTableFilter(tableName, {
    sortField: "identifier",
    reqSortField: "identifier",
    sortOrder: 1,
  });

  const [getFilteredDevicesData, { data, previousData, loading }] = useGetDevicesPagedLazyQuery({
    onError: apolloErrorToaster,
  });

  const [getIsolatedData] = useGetDevicesPagedLazyQuery();

  useEffect(() => {
    if (userSettings) {
      getFilteredDevicesData({
        variables: {
          input: {
            projectId,
            take: tableDetails?.rows || 10,
            skip: tableOptions.first,
            sort: [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: tableOptions.andFilters,
            filter: tableOptions.filter,
          },
        },
      });
    }
  }, [userSettings, tableDetails, projectId, tableOptions, getFilteredDevicesData]);
  const edit = (rowData: MdmDeviceDto) => (
    <Link to={`${rowData.id}`} title={T("mdm.device-info")}>
      {rowData.identifier}
    </Link>
  );

  const handleLastMeasurementDate = useCallback(
    (rowData: MdmDeviceDto) =>
      rowData.status.lastMeasurementDate
        ? fmtDatetime(rowData.status.lastMeasurementDate, tz.display, DATE_FORMATTER_SECONDS)
        : "-",
    [tz.display]
  );

  const deviceData: MdmDeviceDto[] = data?.[dataKey]?.items || [];
  const previousDeviceData: MdmDeviceDto[] = previousData?.[dataKey]?.items || [];
  const tableData = data ? deviceData : previousDeviceData;

  const total = data?.[dataKey]?.pageInfo?.total ?? previousData?.[dataKey]?.pageInfo?.total;

  const measurementKeys = useMemo(
    () => [
      ...(project?.mdmProjectById.deviceConfigs?.reduce((acc, curr) => {
        if (curr?.measurementMappings) {
          curr.measurementMappings.forEach(m => acc.add(m?.key!));
        }
        return acc;
      }, new Set<string>()) ?? []),
    ],
    [project?.mdmProjectById.deviceConfigs]
  );

  const columns: TableColumn<MdmDeviceDto>[] = useMemo(
    () => [
      {
        field: "identifier",
        header: T("mdm.serial-no"),
        body: edit,
        sortable: true,
        columnKey: "identifier",
      },
      {
        field: "objectRelation.mdmObject.identifier",
        header: T("mdm.object-code"),
        body: rowData => renderObjectLinkInDevices(rowData, projectId!),
        sortable: true,
        columnKey: "Object Code",
      },
      {
        field: "objectRelation.mdmObject.address.fullAddress",
        header: T("word.address"),
        sortable: true,
        columnKey: "Object Address",
      },
      {
        field: "objectRelation.roleInObject",
        header: T("mdm.role"),
        body: rowData =>
          project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.find(
            type => type?.id === rowData.objectRelation?.roleInObject
          )?.title ?? "",
        sortable: true,
        columnKey: "Type",
      },
      {
        field: "config.title",
        header: T("word.config"),
        sortable: true,
        columnKey: "Config",
      },
      {
        field: "status.status",
        header: T("word.status"),
        body: renderDeviceStatus,
        sortable: true,
        hideInFile: true,
        columnKey: "Status",
      },
      {
        field: "datasetCount",
        header: T("word.datasets"),
        sortable: true,
        columnKey: "Datasets",
      },
      {
        field: "status.lastMeasurementDate",
        header: T("word.last-measurement-date"),
        body: handleLastMeasurementDate,
        sortable: true,
        columnKey: "Last Measurement Date",
      },
      ...measurementKeys
        .sort((a, b) => {
          const indexA = measurementOrder.indexOf(a);
          const indexB = measurementOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        .map(key => ({
          field: `lastMeasurements.${key}` as keyof MdmDeviceDto,
          header: `${key}${measurementUnits(key) ? ` (${measurementUnits(key)})` : ""}`,
          body: (rowData: MdmDeviceDto) => renderLastMeasurementValue(rowData, key),
          columnKey: key,
          sortable: true,
        })),
    ],
    [
      handleLastMeasurementDate,
      measurementKeys,
      project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes,
      projectId,
    ]
  );

  const formattedData = useMemo(
    () =>
      tableData.map(
        row =>
          ({
            ...row,
            lastMeasurements: row.lastMeasurements?.reduce(
              (acc, m) => ({ ...acc, [m?.mapping as string]: m?.value }),
              {}
            ),
          }) as MdmDeviceDto
      ),
    [tableData]
  );

  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header={T("word.table")}>
        <ObjectAndDeviceTableWrapper
          columns={columns}
          data={formattedData}
          containerName="MdmDevice"
          tableName={tableName}
          loading={loading}
          setTableOptions={setTableOptions}
          tableOptions={tableOptions}
          totalItems={total || 0}
          getQueryData={getFilteredDevicesData}
          getIsolatedData={getIsolatedData}
        />
      </TabPanel>
      <TabPanel header={T("word.map")}>
        <DeviceProvider
          tableOptions={tableOptions}
          records={total ?? 100}
          useGetDevicesPagedQuery={useGetDevicesPagedQuery}
          dataKey={dataKey}
        />
      </TabPanel>
    </TabView>
  );
};

type DeviceProviderProps = {
  tableOptions: DataTableStateEvent;
  records: number;
  useGetDevicesPagedQuery: any;
  dataKey: string;
};
const DeviceProvider: React.FC<DeviceProviderProps> = ({
  tableOptions,
  records,
  useGetDevicesPagedQuery,
  dataKey,
}) => {
  const { projectId } = useProjectContext();

  const { data, loading } = useGetDevicesPagedQuery({
    variables: {
      input: {
        projectId,
        skip: 0,
        take: records,
        andFilters: tableOptions.andFilters,
        filter: tableOptions.filter,
      },
    },
  });

  return (
    <MapWrapper>
      {loading && <ProgressSpinner className="mt-8 w-full" style={{ zIndex: 400 }} />}
      <ObjectMapContent
        objects={[
          ...new Set(
            (data?.[dataKey]?.items as MdmDeviceDto[])?.map(t => t?.objectRelation?.mdmObject as MdmObjectDto)
          ),
        ]}
      />
    </MapWrapper>
  );
};

export default DevicesContent;
